// IndexedDB adapter for the browser / webview build. One object store per table,
// plus `_meta` and `_snapshots`. IndexedDB transactions are atomic across all object
// stores they cover, so a changeset either lands completely or not at all.
import { emptyDataset, TABLE_NAMES, type Changeset, type Dataset, type TableName } from '@vero/domain';
import { StorageError, type FileMeta, type SnapshotMeta, type StorageAdapter } from './adapter.ts';
import { LATEST_VERSION, MIGRATIONS } from './migrations.ts';

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
}
function done(tx: IDBTransaction): Promise<void> {
  return new Promise((res, rej) => {
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error ?? new Error('transaction error'));
    tx.onabort = () => rej(tx.error ?? new Error('transaction aborted'));
  });
}
function wrap(e: unknown): StorageError {
  const name = (e as DOMException)?.name;
  return new StorageError(name === 'QuotaExceededError' ? 'storage.quota' : 'storage.failed', String((e as Error)?.message ?? e), { cause: e });
}

export class IdbAdapter implements StorageAdapter {
  readonly engine = 'indexeddb';
  private db!: IDBDatabase;
  private name: string;
  constructor(name = 'vero-os') { this.name = name; }

  async open() {
    if (this.db) return { schemaVersion: LATEST_VERSION, created: false }; // idempotent (the lock screen opens the store before the workspace)
    let created = false;
    this.db = await new Promise<IDBDatabase>((resolve, reject) => {
      const r = indexedDB.open(this.name, LATEST_VERSION);
      r.onupgradeneeded = (ev) => {
        const db = r.result;
        const old = (ev as IDBVersionChangeEvent).oldVersion;
        created = old === 0;
        for (const s of ['_meta', '_snapshots', '_files']) if (!db.objectStoreNames.contains(s)) db.createObjectStore(s);
        for (const m of MIGRATIONS) if (m.version > old) for (const s of m.idbStores) if (!db.objectStoreNames.contains(s)) db.createObjectStore(s, { keyPath: 'id' });
      };
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
      r.onblocked = () => reject(new StorageError('storage.blocked', 'Another AVRASTRO window is upgrading the database'));
    });
    this.db.onversionchange = () => this.db.close();
    // persistent storage request (reduces eviction risk); best effort
    try { await (navigator as Navigator & { storage?: StorageManager }).storage?.persist?.(); } catch { /* optional */ }
    await this.setMeta('schemaVersion', LATEST_VERSION);
    return { schemaVersion: LATEST_VERSION, created };
  }

  async loadAll(): Promise<Dataset> {
    const tx = this.db.transaction(TABLE_NAMES, 'readonly');
    const ds = emptyDataset() as Record<TableName, unknown[]>;
    await Promise.all(TABLE_NAMES.map(async (t) => { ds[t] = await req(tx.objectStore(t).getAll()); }));
    return ds as Dataset;
  }

  async commit(cs: Changeset) {
    const stores = [...new Set([...cs.puts.map((p) => p.table), ...cs.deletes.map((d) => d.table)])];
    if (!stores.length) return;
    try {
      const tx = this.db.transaction(stores, 'readwrite', { durability: 'strict' } as IDBTransactionOptions);
      const finished = done(tx);
      for (const d of cs.deletes) tx.objectStore(d.table).delete(d.id);
      for (const p of cs.puts) tx.objectStore(p.table).put(p.row);
      await finished;
    } catch (e) { throw wrap(e); }
  }

  async replaceAll(ds: Dataset) {
    try {
      const tx = this.db.transaction(TABLE_NAMES, 'readwrite', { durability: 'strict' } as IDBTransactionOptions);
      const finished = done(tx);
      for (const t of TABLE_NAMES) {
        const store = tx.objectStore(t);
        store.clear();
        for (const r of ds[t] ?? []) store.put(r);
      }
      // verify counts inside the same transaction; abort → nothing changes
      const counts = await Promise.all(TABLE_NAMES.map((t) => req(tx.objectStore(t).count())));
      const bad = TABLE_NAMES.find((t, i) => counts[i] !== (ds[t] ?? []).length);
      if (bad) { tx.abort(); await finished.catch(() => undefined); throw new StorageError('storage.verify_failed', bad); }
      await finished;
    } catch (e) { throw e instanceof StorageError ? e : wrap(e); }
  }

  async getMeta<T>(key: string) { return (await req(this.db.transaction('_meta').objectStore('_meta').get(key))) as T | undefined; }
  async setMeta(key: string, value: unknown) {
    const tx = this.db.transaction('_meta', 'readwrite'); const f = done(tx); tx.objectStore('_meta').put(value, key); await f;
  }
  async saveSnapshot(meta: SnapshotMeta, content: string) {
    try { const tx = this.db.transaction('_snapshots', 'readwrite'); const f = done(tx); tx.objectStore('_snapshots').put({ meta, content }, meta.id); await f; }
    catch (e) { throw wrap(e); }
  }
  async listSnapshots() {
    const all = (await req(this.db.transaction('_snapshots').objectStore('_snapshots').getAll())) as { meta: SnapshotMeta }[];
    return all.map((x) => x.meta).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }
  async readSnapshot(id: string) { const r = (await req(this.db.transaction('_snapshots').objectStore('_snapshots').get(id))) as { content: string } | undefined; return r?.content; }
  async deleteSnapshot(id: string) { const tx = this.db.transaction('_snapshots', 'readwrite'); const f = done(tx); tx.objectStore('_snapshots').delete(id); await f; }
  async putFile(meta: FileMeta, content: Uint8Array) {
    try { const tx = this.db.transaction('_files', 'readwrite', { durability: 'strict' } as IDBTransactionOptions); const f = done(tx); tx.objectStore('_files').put({ meta, content }, meta.id); await f; }
    catch (e) { throw wrap(e); }
  }
  async getFile(id: string) { return (await req(this.db.transaction('_files').objectStore('_files').get(id))) as { meta: FileMeta; content: Uint8Array } | undefined; }
  async deleteFile(id: string) { const tx = this.db.transaction('_files', 'readwrite'); const f = done(tx); tx.objectStore('_files').delete(id); await f; }
  async listFiles() { const all = (await req(this.db.transaction('_files').objectStore('_files').getAll())) as { meta: FileMeta }[]; return all.map((x) => x.meta); }
  /** raw objects as stored (diagnostics / encryption-at-rest checks) */
  async rawRows(table: TableName) { return req(this.db.transaction(table).objectStore(table).getAll()); }
  async close() { this.db?.close(); }
}
